import React, { useState, useEffect, useRef, useMemo } from 'react';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, 
  Plus, 
  Search, 
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  X, 
  CheckCircle,
  Clock,
  TrendingUp, 
  DollarSign,
  Filter,
  ChevronDown,
  ChevronUp,
  Calendar,
  List,
  Grid,
  RefreshCcw,
  LayoutGrid,
  Shield,
  Layers,
  ClipboardList,
} from 'lucide-react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import localApiAdapter from '../../core/services/localApiAdapter';
import { mapClosureFromApi, mapClosureToApi } from './treasuryApiMappers';
import {
  closureExportHeaders,
  mapClosuresForExport,
  exportTreasuryCSV,
  exportTreasuryExcel,
  exportTreasuryPDF,
  closureImportHeaders,
  triggerTreasuryImport,
  parseTreasuryImport,
} from './treasuryExportHelpers';
import toast from 'react-hot-toast';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyCreated, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import '../../shared/components/custom/CustomComponents.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  QAWAEM_MODAL_OVERLAY_CLASS,
  getQawaemModalPanelClass,
  getQawaemModalPanelStyle,
  QawaemErpViewModal,
} from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { buildCaisseColumnDefs, useCaisseColumnVisibility, caisseTableColSpan, buildCaisseModalFormTabs, nextCaisseEntityRef, sliceCaisseListItems, useCaisseEmployeeOptions, buildCaisseEmployeeSelectOptions, resolveCaisseAccountBalance, CAISSE_FORM_GRID } from './caisseListPageUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import QawaemImportExportToolbar from '../../products/saudi/components/QawaemImportExportToolbar';

const CaisseClosurePage = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const treasuryConfig = useTreasuryConfig();
  const [viewMode, setViewMode] = useTreasuryListViewMode(treasuryConfig);
  const p = (key, opts) => t(`modules.caisse.pages.caisseClosurePage.${key}`, opts);
  const { employeeOptions } = useCaisseEmployeeOptions({ valueField: 'name' });
  const cashierOptions = useMemo(
    () => buildCaisseEmployeeSelectOptions(employeeOptions, { emptyLabel: p('placeholder_cashier'), valueField: 'name' }),
    [employeeOptions, p],
  );
  const exportP = (key, opts) => t(`modules.caisse.pages.treasuryExportHelpers.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'الإغلاق' });

  const formatCurrency = (amount) => formatBrandMoney(amount);
  const [closures, setClosures] = useState([]);
  const [defaultCaisseId, setDefaultCaisseId] = useState(1);
  const [principalBalance, setPrincipalBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentView, setCurrentView] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClosure, setSelectedClosure] = useState(null);
  
  const [closureFormTab, setClosureFormTab] = useState('general');

  const closureFormTabDefs = useMemo(() => ([
    { id: 'general', label: p('section_general'), icon: FileText, iconClassName: 'text-emerald-400' },
    { id: 'amounts', label: p('section_amounts'), icon: DollarSign, iconClassName: 'text-amber-400' },
    { id: 'verification', label: p('section_verification'), icon: CheckCircle, iconClassName: 'text-blue-400' },
  ]), [p]);
  const CLOSURE_FIELD_COUNT = 8;
  const closureFormTabs = useMemo(
    () => buildCaisseModalFormTabs(closureFormTabDefs, CLOSURE_FIELD_COUNT),
    [closureFormTabDefs],
  );
  
  // New UI Design States
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [mainViewMode, setMainViewMode] = useState('all'); // 'all', 'par_statut', 'par_periode'
  const [quickFilter, setQuickFilter] = useState('all'); // 'all', 'closed', 'pending'
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    reference: '',
    caissier: '',
    soldeOuverture: 0,
    totalEntrees: 0,
    totalSorties: 0,
    soldeFermeture: 0,
    soldeReel: 0,
    ecart: 0,
    notes: '',
    status: 'pending'
  });

  useEffect(() => {
    loadClosures();
  }, []);
  
  // Handle outside clicks for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadClosures = async () => {
    setLoading(true);
    try {
      const [rows, dashboard] = await Promise.all([
        localApiAdapter.get('/treasury/closure').catch(() => []),
        localApiAdapter.get('/tresorerie/position/by-type/caisse').catch(() => null),
      ]);
      const principal = (dashboard?.accounts || dashboard?.balanceByCaisse || []).find((c) => c.type === 'principal');
      if (principal?.id) setDefaultCaisseId(principal.id);
      setPrincipalBalance(resolveCaisseAccountBalance(principal));
      setClosures(sortRecordsNewestFirst((Array.isArray(rows) ? rows : []).map(mapClosureFromApi)));
    } catch (error) {
      console.error('Failed to load closures:', error);
      toast.error(p('load_error'));
      setClosures([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateClosure = (ouverture, entrees, sorties) => {
    return parseFloat(ouverture) + parseFloat(entrees) - parseFloat(sorties);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const soldeFermeture = calculateClosure(formData.soldeOuverture, formData.totalEntrees, formData.totalSorties);
    const ecart = parseFloat(formData.soldeReel) - soldeFermeture;

    try {
      if (selectedClosure?.id) {
        await localApiAdapter.patch(`/treasury/closure/${selectedClosure.id}/status`, {
          status: formData.status === 'closed' ? 'approved' : 'pending',
          approved_by: formData.caissier || null,
        });
        notifyUpdated(showNotify, entityLabel);
      } else {
        const payload = mapClosureToApi({ ...formData, caisse_id: defaultCaisseId }, defaultCaisseId);
        await localApiAdapter.post('/treasury/closure', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadClosures();
      resetForm();
    } catch (error) {
      console.error('Closure save error:', error);
      notifyError(showNotify, error.message || p('load_error'));
    }
  };

  const handleView = (closure) => {
    setSelectedClosure(closure);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ date: new Date().toISOString().split('T')[0], reference: '', caissier: '', soldeOuverture: 0, totalEntrees: 0, totalSorties: 0, soldeFermeture: 0, soldeReel: 0, ecart: 0, notes: '', status: 'pending' });
    setSelectedClosure(null);
    setClosureFormTab('general');
    setShowModal(false);
  };

  const openModal = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      reference: nextCaisseEntityRef(
        settings,
        'closure',
        treasuryConfig.closurePrefix || 'CLS',
        closures,
        (c) => c.reference,
      ),
      caissier: '',
      soldeOuverture: principalBalance || 0,
      totalEntrees: 0,
      totalSorties: 0,
      soldeFermeture: principalBalance || 0,
      soldeReel: 0,
      ecart: 0,
      notes: '',
      status: 'pending',
    });
    setClosureFormTab('general');
    setShowModal(true);
  };
  
  // Handle select all
  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredRowIds);
    }
  };

  // Handle item select
  const handleItemSelect = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };
  
  const mainTabs = [
    { id: 'all', nameKey: 'tab_general', icon: LayoutGrid },
    { id: 'par_statut', nameKey: 'tab_by_status', icon: Shield }
  ];

  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Lock },
    { id: 'closed', nameKey: 'filter_closed', icon: CheckCircle },
    { id: 'pending', nameKey: 'filter_pending', icon: TrendingUp }
  ];

  const statusOptions = [
    { value: 'all', label: p('status_all') },
    { value: 'closed', label: p('status_closed') },
    { value: 'pending', label: p('status_pending') }
  ];

  const formStatusOptions = [
    { value: 'pending', label: p('status_pending') },
    { value: 'closed', label: p('status_closed') }
  ];

  const clickableStatusOptions = useMemo(() => ([
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'closed', label: p('status_closed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
  ]), [p]);

  const closureColumnKeys = useMemo(
    () => ['code', 'date', 'cashier', 'theoreticalBalance', 'actualBalance', 'discrepancy', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useCaisseColumnVisibility(closureColumnKeys);
  const qawaemColumns = useMemo(
    () => buildCaisseColumnDefs([
      { key: 'code', labelKey: 'col_code' },
      { key: 'date', labelKey: 'col_date' },
      { key: 'cashier', labelKey: 'col_cashier' },
      { key: 'theoreticalBalance', labelKey: 'col_theoretical_balance' },
      { key: 'actualBalance', labelKey: 'col_actual_balance' },
      { key: 'discrepancy', labelKey: 'col_discrepancy' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = caisseTableColSpan(qawaemColumns);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.patch(`/treasury/closure/${recordId}/status`, {
        status: newStatus === 'closed' ? 'approved' : 'pending',
      });
      notifyUpdated(showNotify, entityLabel);
      await loadClosures();
    } catch (error) {
      console.error('Closure status change error:', error);
      notifyError(showNotify, error.message || t('common.error'));
    }
  };

  const columnFilterRules = useMemo(() => [
    textColumnRule('code', (c) => c.reference || ''),
    textColumnRule('date', (c) => new Date(c.date).toLocaleDateString(dateLocale)),
    textColumnRule('cashier', (c) => c.caissier || ''),
    textColumnRule('theoreticalBalance', (c) => formatCurrency(c.soldeFermeture)),
    textColumnRule('actualBalance', (c) => formatCurrency(c.soldeReel)),
    textColumnRule('discrepancy', (c) => formatCurrency(c.ecart)),
    exactColumnRule('status', (c) => c.status || ''),
  ], [dateLocale, formatCurrency]);

  const filteredClosures = useMemo(() => {
    const base = closures.filter((closure) => {
      const matchesView = currentView === 'all' || closure.status === currentView;
      const matchesSearch = !searchTerm
        || closure.caissier?.toLowerCase().includes(searchTerm.toLowerCase())
        || closure.reference?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesView && matchesSearch;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [closures, currentView, searchTerm, columnFilters, columnFilterRules]);

  const pagedClosures = useMemo(
    () => sliceCaisseListItems(filteredClosures, treasuryConfig.itemsPerPage),
    [filteredClosures, treasuryConfig.itemsPerPage],
  );

  const filteredRowIds = useMemo(() => filteredClosures.map((r) => r.id), [filteredClosures]);
  const allRowsSelected = filteredRowIds.length > 0 && filteredRowIds.every((id) => selectedItems.includes(id));
  const someRowsSelected = selectedItems.length > 0 && !allRowsSelected;

  const exportRows = mapClosuresForExport(filteredClosures);
  const exportHeaders = closureExportHeaders(exportP);
  const exportBaseName = `treasury_closures_${new Date().toISOString().slice(0, 10)}`;

  const handleExportCSV = () => {
    if (!exportRows.length) { toast.error(p('empty_message') || p('load_error')); return; }
    exportTreasuryCSV(exportRows, exportHeaders, `${exportBaseName}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    if (!exportRows.length) { toast.error(p('empty_message') || p('load_error')); return; }
    await exportTreasuryExcel(exportRows, exportHeaders, `${exportBaseName}.xlsx`, p('title'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    if (!exportRows.length) { toast.error(p('empty_message') || p('load_error')); return; }
    setIsGeneratingPDF(true);
    try {
      await exportTreasuryPDF(p('title'), exportHeaders, exportRows);
    } finally {
      setIsGeneratingPDF(false);
      setShowExportDropdown(false);
    }
  };

  const handleImportFile = (file) => {
    const headers = closureImportHeaders(exportP);
    const schema = {
      requiredFields: ['date'],
      validators: {
        soldeOuverture: (v) => !v || !Number.isNaN(Number(v)),
        totalEntrees: (v) => !v || !Number.isNaN(Number(v)),
        totalSorties: (v) => !v || !Number.isNaN(Number(v)),
        soldeReel: (v) => !v || !Number.isNaN(Number(v)),
      },
    };
    parseTreasuryImport(file, headers, schema)
      .then(async (result) => {
        const rows = result?.data || result?.rows || [];
        if (!rows.length) {
          toast.error(p('empty_message'));
          return;
        }
        let imported = 0;
        for (const row of rows) {
          const payload = mapClosureToApi({
            date: row.date,
            soldeOuverture: Number(row.soldeOuverture) || 0,
            totalEntrees: Number(row.totalEntrees) || 0,
            totalSorties: Number(row.totalSorties) || 0,
            soldeReel: Number(row.soldeReel) || 0,
            caisse_id: defaultCaisseId,
          }, defaultCaisseId);
          await localApiAdapter.post('/treasury/closure', payload);
          imported += 1;
        }
        toast.success(p('import_success', { count: imported }));
        await loadClosures();
      })
      .catch((err) => {
        console.error('Import closures error:', err);
        toast.error(err.message || p('load_error'));
      })
      .finally(() => setShowImportDropdown(false));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Lock className="h-8 w-8" />
            {p('title')}
            <button onClick={loadClosures} className="refresh-button ml-4" title={p('refresh')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3 items-center">
          <QawaemImportExportToolbar
            importOpen={showImportDropdown}
            exportOpen={showExportDropdown}
            onToggleImport={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }}
            onToggleExport={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }}
            importRef={importDropdownRef}
            exportRef={exportDropdownRef}
            importLabel={p('import', { defaultValue: 'استيراد' })}
            exportLabel={p('export', { defaultValue: 'تصدير' })}
            generatingLabel={t('common.loading')}
            isGeneratingPDF={isGeneratingPDF}
            onImportCSV={() => { triggerTreasuryImport(handleImportFile, '.csv'); setShowImportDropdown(false); }}
            onImportExcel={() => { triggerTreasuryImport(handleImportFile); setShowImportDropdown(false); }}
            onExportCSV={handleExportCSV}
            onExportExcel={handleExportExcel}
            onExportPDF={handleExportPDF}
          />
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions 
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400' 
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={p('advanced_options')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? (
              <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
            ) : (
              <ChevronDown className="h-4 w-4 transition-transform" />
            )}
          </button>
          
          <button
            onClick={openModal}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_closure')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { labelKey: 'stat_total', value: closures.length, icon: ClipboardList, color: 'from-blue-500 to-blue-600' },
          { labelKey: 'stat_closed', value: closures.filter(c => c.status === 'closed').length, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { labelKey: 'stat_pending', value: closures.filter(c => c.status === 'pending').length, icon: TrendingUp, color: 'from-orange-500 to-orange-600' },
          { labelKey: 'stat_discrepancies', value: formatCurrency(closures.reduce((sum, c) => sum + Math.abs(c.ecart), 0)), icon: DollarSign, color: 'from-purple-500 to-purple-600' },
          { labelKey: 'stat_compliance_rate', value: `${closures.length > 0 ? ((closures.filter(c => c.ecart === 0).length / closures.length * 100).toFixed(1)) : 0}%`, icon: Shield, color: 'from-cyan-500 to-cyan-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{p(stat.labelKey)}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Options Avancées - Collapsible */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-4">
              {/* Main View Tabs */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  {mainTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setMainViewMode(tab.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                        mainViewMode === tab.id
                          ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span className="hidden sm:inline">{p(tab.nameKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Filter Buttons */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto table-scroll">
                  {quickFilterButtons.map((btn) => (
                    <button
                      key={btn.id}
                      onClick={() => {
                        setQuickFilter(btn.id);
                        setCurrentView(btn.id);
                      }}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                        quickFilter === btn.id
                          ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <btn.icon className="h-5 w-5" />
                      <span className="hidden sm:inline">{p(btn.nameKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Distribution Overview */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <h3 className="text-xl font-semibold text-white mb-4">{p('distribution_title')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('dist_closed')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-gradient-to-r from-green-500 to-green-600 text-white force-white">
                        {closures.filter(c => c.status === 'closed').length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-gradient-to-r from-green-500 to-green-600" style={{ width: `${closures.length > 0 ? (closures.filter(c => c.status === 'closed').length / closures.length * 100) : 0}%` }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_of_closures', { percent: closures.length > 0 ? ((closures.filter(c => c.status === 'closed').length / closures.length * 100).toFixed(1)) : 0 })}</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('dist_pending')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white force-white">
                        {closures.filter(c => c.status === 'pending').length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600" style={{ width: `${closures.length > 0 ? (closures.filter(c => c.status === 'pending').length / closures.length * 100) : 0}%` }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_of_closures', { percent: closures.length > 0 ? ((closures.filter(c => c.status === 'pending').length / closures.length * 100).toFixed(1)) : 0 })}</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('dist_total')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white force-white">
                        {closures.length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600" style={{ width: '100%' }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_all_closures')}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' ? (
          <div className="overflow-x-auto table-horizontal-scroll">
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColumnVisible('code') ? (
                    <TableColumnHeaderFilter columnKey="code" label={p('col_code')} value={columnFilters.code || ''} onChange={(v) => setFilter('code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('date') ? (
                    <TableColumnHeaderFilter columnKey="date" label={p('col_date')} value={columnFilters.date || ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('cashier') ? (
                    <TableColumnHeaderFilter columnKey="cashier" label={p('col_cashier')} value={columnFilters.cashier || ''} onChange={(v) => setFilter('cashier', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('theoreticalBalance') ? (
                    <TableColumnHeaderFilter columnKey="theoreticalBalance" label={p('col_theoretical_balance')} value={columnFilters.theoreticalBalance || ''} onChange={(v) => setFilter('theoreticalBalance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('actualBalance') ? (
                    <TableColumnHeaderFilter columnKey="actualBalance" label={p('col_actual_balance')} value={columnFilters.actualBalance || ''} onChange={(v) => setFilter('actualBalance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('discrepancy') ? (
                    <TableColumnHeaderFilter columnKey="discrepancy" label={p('col_discrepancy')} value={columnFilters.discrepancy || ''} onChange={(v) => setFilter('discrepancy', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('status') ? (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={[{ value: 'closed', label: p('status_closed') }, { value: 'pending', label: p('status_pending') }]} value={columnFilters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {pagedClosures.map((closure) => (
                  <tr key={closure.id} className="hover:bg-white/5 transition-colors">
                    <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                      <div className="flex justify-center">
                        <QawaemTableCheckbox
                          checked={selectedItems.includes(closure.id)}
                          onChange={() => handleItemSelect(closure.id)}
                        />
                      </div>
                    </td>
                    {isColumnVisible('code') ? (
                      <td className="px-6 py-4 text-white font-mono text-sm">{closure.reference}</td>
                    ) : null}
                    {isColumnVisible('date') ? (
                      <td className="px-6 py-4 text-white font-medium">{new Date(closure.date).toLocaleDateString(dateLocale)}</td>
                    ) : null}
                    {isColumnVisible('cashier') ? (
                      <td className="px-6 py-4 text-white/80">{closure.caissier}</td>
                    ) : null}
                    {isColumnVisible('theoreticalBalance') ? (
                      <td className="px-6 py-4 text-white font-bold">{formatCurrency(closure.soldeFermeture)}</td>
                    ) : null}
                    {isColumnVisible('actualBalance') ? (
                      <td className="px-6 py-4 text-white font-bold">{formatCurrency(closure.soldeReel)}</td>
                    ) : null}
                    {isColumnVisible('discrepancy') ? (
                      <td className="px-6 py-4">
                        <span className={`font-bold ${closure.ecart === 0 ? 'text-green-400' : closure.ecart > 0 ? 'text-blue-400' : 'text-red-400'}`}>
                          {closure.ecart > 0 ? '+' : ''}{formatCurrency(closure.ecart)}
                        </span>
                      </td>
                    ) : null}
                    {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={closure.status}
                            recordId={closure.id}
                            options={clickableStatusOptions}
                            onChange={handleStatusChange}
                            title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <td className="px-6 py-4 text-center align-middle">
                        <QawaemErpRowActions
                        onView={() => handleView(closure)}
                        viewTitle={p('view')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {pagedClosures.map((closure) => (
                <motion.div
                  key={closure.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 h-full min-w-0"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                          {new Date(closure.date).toLocaleDateString(dateLocale)}
                        </span>
                        <QawaemClickableStatusBadge
                          status={closure.status}
                          recordId={closure.id}
                          options={clickableStatusOptions}
                          onChange={handleStatusChange}
                        />
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-1">{closure.caissier}</h3>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('col_theoretical_balance')}</span>
                      <span className="text-white font-semibold">{formatCurrency(closure.soldeFermeture)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('col_actual_balance')}</span>
                      <span className="text-white font-semibold">{formatCurrency(closure.soldeReel)}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-white/10">
                      <span className="text-white/50 text-xs">{p('col_discrepancy')}</span>
                      <span className={`font-bold text-lg ${closure.ecart === 0 ? 'text-green-400' : closure.ecart > 0 ? 'text-blue-400' : 'text-red-400'}`}>
                        {closure.ecart > 0 ? '+' : ''}{formatCurrency(closure.ecart)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-center pt-2 border-t border-white/10">
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(closure)}
                      viewTitle={p('view')}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal - Style Journal de Caisse (100% identique) */}
      {showModal && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} onClick={resetForm} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={getQawaemModalPanelClass(isLight)}
            style={getQawaemModalPanelStyle(isLight, '1000px')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${cfgTheme.title}`}>{p('modal_new_title')}</h2>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  form="cloture-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {p('create_closure')}
                </button>
                <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors" aria-label={p('close_aria')}>
                  <X size={24} />
                </button>
              </div>
            </div>
            {closureFormTabs.length > 0 ? (
              <QawaemErpModalFormTabBar
                tabs={closureFormTabs}
                activeTab={closureFormTab}
                onTabChange={setClosureFormTab}
                isLight={isLight}
                ariaLabel={p('modal_new_title')}
              />
            ) : null}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="cloture-form" onSubmit={handleSubmit}>
                {closureFormTabs.length === 0 ? (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label>
                      <CustomDatePicker value={formData.date} onChange={(value) => setFormData({ ...formData, date: value })} placeholder={p('label_date_select')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('col_code', { defaultValue: 'رمز' })} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.reference || ''} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} placeholder={p('col_code', { defaultValue: 'رمز' })} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_cashier')} <span className="qawaem-required-mark">*</span></label>
                      <CustomSelect value={formData.caissier} onChange={(value) => setFormData({ ...formData, caissier: value })} options={cashierOptions} placeholder={p('placeholder_cashier')} searchable={true} creatable={false} autoSelect={false} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_opening_balance')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" step="0.01" required value={formData.soldeOuverture} onChange={(e) => setFormData({ ...formData, soldeOuverture: e.target.value })} placeholder={p('placeholder_amount')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_total_in')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" step="0.01" required value={formData.totalEntrees} onChange={(e) => setFormData({ ...formData, totalEntrees: e.target.value })} placeholder={p('placeholder_amount')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_total_out')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" step="0.01" required value={formData.totalSorties} onChange={(e) => setFormData({ ...formData, totalSorties: e.target.value })} placeholder={p('placeholder_amount')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_actual_balance')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" step="0.01" required value={formData.soldeReel} onChange={(e) => setFormData({ ...formData, soldeReel: e.target.value })} placeholder={p('placeholder_amount')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_status')}</label>
                      <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={formStatusOptions} placeholder={p('placeholder_status')} />
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_notes')}</label>
                      <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder={p('placeholder_notes')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none" />
                    </div>
                  </div>
                ) : null}
                {closureFormTabs.length > 0 && closureFormTab === 'general' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label>
                      <CustomDatePicker value={formData.date} onChange={(value) => setFormData({ ...formData, date: value })} placeholder={p('label_date_select')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('col_code', { defaultValue: 'رمز' })} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.reference || ''} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} placeholder={p('col_code', { defaultValue: 'رمز' })} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_cashier')} <span className="qawaem-required-mark">*</span></label>
                      <CustomSelect value={formData.caissier} onChange={(value) => setFormData({ ...formData, caissier: value })} options={cashierOptions} placeholder={p('placeholder_cashier')} searchable={true} creatable={false} autoSelect={false} />
                    </div>
                  </div>
                )}
                {closureFormTabs.length > 0 && closureFormTab === 'amounts' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_opening_balance')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" step="0.01" required value={formData.soldeOuverture} onChange={(e) => setFormData({ ...formData, soldeOuverture: e.target.value })} placeholder={p('placeholder_amount')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_total_in')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" step="0.01" required value={formData.totalEntrees} onChange={(e) => setFormData({ ...formData, totalEntrees: e.target.value })} placeholder={p('placeholder_amount')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_total_out')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" step="0.01" required value={formData.totalSorties} onChange={(e) => setFormData({ ...formData, totalSorties: e.target.value })} placeholder={p('placeholder_amount')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                  </div>
                )}
                {closureFormTabs.length > 0 && closureFormTab === 'verification' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_actual_balance')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" step="0.01" required value={formData.soldeReel} onChange={(e) => setFormData({ ...formData, soldeReel: e.target.value })} placeholder={p('placeholder_amount')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_status')}</label>
                      <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={formStatusOptions} placeholder={p('placeholder_status')} />
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('label_notes')}</label>
                      <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder={p('placeholder_notes')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none" />
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <p className="text-blue-300 text-sm">{p('documents_hint')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showViewModal && selectedClosure ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => { setShowViewModal(false); setSelectedClosure(null); }}
          title={p('modal_view_title')}
          icon={Lock}
          maxWidth="700px"
        >
            <div className="space-y-4">
              <div className={CAISSE_FORM_GRID}>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('col_date')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{new Date(selectedClosure.date).toLocaleDateString(dateLocale)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('col_cashier')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{selectedClosure.caissier}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('label_status')}</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedClosure.status === 'closed' ? 'bg-green-500/20 text-green-600' : 'bg-amber-500/20 text-amber-600'
                  }`}>
                    {selectedClosure.status === 'closed' ? p('status_closed') : p('status_pending')}
                  </span>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('label_opening_balance_short')}</p>
                  <p className={`font-bold ${cfgTheme.title}`}>{formatCurrency(selectedClosure.soldeOuverture)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('label_total_in_short')}</p>
                  <p className="text-green-500 font-bold">+{formatCurrency(selectedClosure.totalEntrees)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('label_total_out_short')}</p>
                  <p className="text-red-500 font-bold">-{formatCurrency(selectedClosure.totalSorties)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('label_theoretical_balance')}</p>
                  <p className={`font-bold text-xl ${cfgTheme.title}`}>{formatCurrency(selectedClosure.soldeFermeture)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('label_actual_balance_short')}</p>
                  <p className={`font-bold text-xl ${cfgTheme.title}`}>{formatCurrency(selectedClosure.soldeReel)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('label_discrepancy')}</p>
                  <p className={`font-bold text-2xl ${selectedClosure.ecart === 0 ? 'text-green-500' : selectedClosure.ecart > 0 ? 'text-blue-500' : 'text-red-500'}`}>
                    {selectedClosure.ecart > 0 ? '+' : ''}{formatCurrency(selectedClosure.ecart)}
                  </p>
                </div>
                {selectedClosure.notes ? (
                  <div className="md:col-span-3">
                    <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('label_notes')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{selectedClosure.notes}</p>
                  </div>
                ) : null}
              </div>
            </div>
        </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default CaisseClosurePage;




